import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    return { success: () => {}, error: () => {}, info: () => {}, warning: () => {} };
  }
  return context;
};

// Imperative API ref — set via useEffect, not during render
const _showRef = { current: null };

export const notify = {
  success: (message, cta) => _showRef.current?.({ message, type: 'success', cta }),
  error: (message, cta) => _showRef.current?.({ message, type: 'error', cta }),
  info: (message, cta) => _showRef.current?.({ message, type: 'info', cta }),
  warning: (message, cta) => _showRef.current?.({ message, type: 'warning', cta }),
};

const defaultCTA = {
  success: 'Understood',
  error: 'Close',
  info: 'Noted',
  warning: 'Okay',
};

const typeConfig = {
  success: { accent: '#10B981', iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', colorClass: 'text-emerald-500' },
  error:   { accent: '#EF4444', iconPath: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', colorClass: 'text-red-500' },
  info:    { accent: '#3B82F6', iconPath: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', colorClass: 'text-blue-500' },
  warning: { accent: '#F59E0B', iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z', colorClass: 'text-amber-500' },
};

const NotificationModal = ({ notification, onDismiss }) => {
  const cfg = typeConfig[notification.type] || typeConfig.info;
  const ctaLabel = notification.cta || defaultCTA[notification.type] || 'Close';
  const btnRef = useRef(null);

  // Focus the CTA button when modal mounts
  useEffect(() => {
    const timer = setTimeout(() => btnRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onDismiss();
  };

  return ReactDOM.createPortal(
    <div
      data-notif-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        animation: 'notifOverlayIn 0.2s ease-out',
        pointerEvents: 'auto',
      }}
      onClick={handleDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: '1rem', maxWidth: '24rem', width: '90%',
          margin: '0 1rem', overflow: 'hidden', position: 'relative', zIndex: 1,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          animation: 'notifModalIn 0.25s ease-out',
          pointerEvents: 'auto',
        }}
      >
        {/* Accent bar */}
        <div style={{ height: '6px', backgroundColor: cfg.accent }} />

        {/* Content */}
        <div style={{ padding: '24px 24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <svg className={`w-8 h-8 ${cfg.colorClass}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ marginBottom: '12px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d={cfg.iconPath} />
          </svg>
          <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.625', fontWeight: 500 }}>
            {notification.message}
          </p>
        </div>

        {/* CTA */}
        <div style={{ padding: '0 24px 20px', display: 'flex', justifyContent: 'center' }}>
          <button
            ref={btnRef}
            type="button"
            onClick={handleDismiss}
            onMouseDown={handleDismiss}
            style={{
              padding: '10px 32px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
              color: '#fff', backgroundColor: cfg.accent, border: 'none',
              cursor: 'pointer', pointerEvents: 'auto', position: 'relative', zIndex: 2,
              transition: 'opacity 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Inject keyframes once
const styleId = 'notif-modal-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes notifOverlayIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes notifModalIn { from { opacity: 0; transform: scale(0.95) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
  `;
  document.head.appendChild(style);
}

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);

  const show = useCallback(({ message, type = 'info', cta }) => {
    setNotification({ message, type, cta });
  }, []);

  const dismiss = useCallback(() => {
    setNotification(null);
  }, []);

  // Register imperative API via useEffect (not during render)
  useEffect(() => {
    _showRef.current = show;
    return () => { _showRef.current = null; };
  }, [show]);

  const contextValue = {
    success: (message, cta) => show({ message, type: 'success', cta }),
    error: (message, cta) => show({ message, type: 'error', cta }),
    info: (message, cta) => show({ message, type: 'info', cta }),
    warning: (message, cta) => show({ message, type: 'warning', cta }),
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {notification && <NotificationModal notification={notification} onDismiss={dismiss} />}
    </NotificationContext.Provider>
  );
};
